import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Loader2Icon, SearchXIcon, TriangleAlertIcon } from "lucide-react";

import { type SearchCandidateResult } from "../searchV2CandidateResult";
import { SearchSheet } from "../sheet/SearchSheet";
import { SearchV2CandidateCard } from "./SearchV2CandidateCard";
import { SearchV2CandidateSkeleton } from "./SearchV2CandidateSkeleton";

import { Column, Row, Text } from "@/components";
import { AgentSchedulingGuardrailDialog } from "@/components/AgentSchedulingGuardrailDialog";

import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useCustomerTopPrioritySummaries } from "@/hooks/useCustomerTopPrioritySummaries";
import { useCreateCandidacy } from "@/hooks/mutations/useCreateCandidacy";
import {
  getSearchPageNumber,
  getSearchPageResults,
  useGetSearchV2Candidates,
  type SearchV2CandidatesParams,
} from "@/hooks/queries/useGetSearchV2Candidates";
import { agentGuardrails } from "@/lib/agentGuardrails";
import { SEARCH_V2_ANALYTICS_METADATA } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { isUserTyping } from "@/lib/utils";
import { searchStore } from "@/stores/search";
import {
  CandidacyStatus,
  CandidacySource,
  ReviewDecision,
  SallyState,
  SearchDecision,
  SearchInterest,
  SearchEventContext,
  SearchInterestChangeOptions,
  SearchLastDecision,
  SessionShortlist,
} from "@/types";
import { SearchPageBreak } from "./SearchPageBreak";

const SKELETON_CARD_COUNT = 2;

// True when the card after `index` came from a later fetch page.
const isPageBoundary = (list: SearchCandidateResult[], index: number) => {
  const next = list.at(index + 1);
  const current = list.at(index);
  return (
    next?.pageNumber !== undefined && next.pageNumber !== current?.pageNumber
  );
};
// Browsers fire a synthetic mousemove when a scroll settles; ignore hover for
// this long after a programmatic scroll so it doesn't steal the active card.
const HOVER_SUPPRESS_AFTER_SCROLL_MS = 800;
const LOAD_MORE_TRIGGER_RATIO = 0.6;

export const SearchV2CandidateList = ({
  hasCustomerTopPriority,
  candidatesOverride,
  focusResetKey,
  overridePaging,
  isAgentActive,
  onLastDecisionChange,
  onSearchInterestChange,
  onShortlisted,
  searchInterests,
  ...searchParams
}: SearchV2CandidatesParams & {
  hasCustomerTopPriority: boolean;
  // When supplied, these ARE the results — the search query is skipped entirely.
  // Lets the Sally mode reuse every card, sheet, and shortlist behaviour here
  // while driving the list from a completely different corpus.
  candidatesOverride?: SearchCandidateResult[];
  // Paging for the override corpus, so it scrolls like search.
  overridePaging?: {
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
  };
  isAgentActive: boolean;
  // Override callers have no search params to watch, so they say when their
  // results are "new" (e.g. Sally state filter) and the first card refocuses.
  focusResetKey?: string;
  // Latest interest/hide and how to revert it; null once the stack empties.
  onLastDecisionChange?: (decision: SearchLastDecision | null) => void;
  onSearchInterestChange: (
    talentId: number,
    isInterested: boolean | null,
    options?: SearchInterestChangeOptions,
  ) => void;
  onShortlisted: (shortlist: SessionShortlist) => void;
  searchInterests: SearchInterest[];
}) => {
  const jobId = useCurrentJobId();

  // Skipped entirely when an override supplies the corpus.
  const searchApi = useGetSearchV2Candidates(searchParams, {
    enabled: !candidatesOverride,
  });
  // Create
  const { mutate: createCandidacy, isPending: isCreatingCandidacy } =
    useCreateCandidacy();
  // Update

  /* React Hooks */
  // Tracks which decision shortlisted each candidate so the clicked state can
  // reflect agent vs manual.
  const [shortlistDecisions, setShortlistDecisions] = useState<
    Map<number, ReviewDecision>
  >(new Map());
  const [candidacyIds, setCandidacyIds] = useState<Map<number, string>>(
    new Map(),
  );
  const [selectedCandidate, setSelectedCandidate] =
    useState<SearchCandidateResult | null>(null);
  const [agentGuardrail, setAgentGuardrail] = useState<{
    candidateId: number;
    reasons: string[];
  } | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  // Keyboard target. Set by real mouse movement or advanced by the hotkeys.
  const [activeTalentId, setActiveTalentId] = useState<number | null>(null);
  const activeTalentIdRef = useRef<number | null>(null);
  activeTalentIdRef.current = activeTalentId;
  // Hotkey decisions, newest last, with the interest state they replaced so
  // U can put it back.
  const undoStackRef = useRef<
    {
      candidateName: string;
      decision: SearchDecision;
      previous: boolean | null;
      talentId: number;
    }[]
  >([]);
  // Scroll to `talentId` once it's rendered and, if set, once `afterRemovalOf`
  // has left the list — hides remove their card only after the interest cache
  // updates, and measuring before that overshoots by a card.
  const pendingScrollRef = useRef<{
    afterRemovalOf?: number;
    talentId: number;
  } | null>(null);
  const suppressHoverUntilRef = useRef(0);

  const scrollCardIntoView = (talentId: number) => {
    suppressHoverUntilRef.current = Date.now() + HOVER_SUPPRESS_AFTER_SCROLL_MS;
    document
      .querySelector(`[data-talent-id="${String(talentId)}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  // Interests queued this session stop occupying result slots once the staffer
  // changes filters/tuning — they asked for new candidates. The queued people
  // stay reviewable (the review list reads the unfiltered corpus), they just
  // no longer render in results.
  const [staleInterestedTalentIds, setStaleInterestedTalentIds] = useState<
    Set<number>
  >(new Set());

  const hiddenTalentIds = useMemo(
    () =>
      new Set(
        searchInterests
          .filter((interest) => !interest.isInterested)
          .map((interest) => interest.talentId),
      ),
    [searchInterests],
  );
  const interestedTalentIds = useMemo(
    () =>
      new Set(
        searchInterests
          .filter((interest) => interest.isInterested)
          .map((interest) => interest.talentId),
      ),
    [searchInterests],
  );
  // Top candidates are auto-marked as search interests by the backend — the
  // flag drives the crown badge wherever they surface in the results.
  const topCandidateTalentIds = useMemo(
    () =>
      new Set(
        searchInterests
          .filter((interest) => interest.isTopCandidate)
          .map((interest) => interest.talentId),
      ),
    [searchInterests],
  );
  const candidates = useMemo(() => {
    // An override replaces the search results wholesale. Hides still apply —
    // "not interested" is a judgement about the person, which holds whatever
    // the corpus is.
    // Each result remembers the fetch page it arrived on so the list can mark
    // page boundaries.
    const corpus =
      candidatesOverride ??
      searchApi.data?.pages.flatMap((page) =>
        getSearchPageResults(page).map((candidate) => ({
          ...candidate,
          pageNumber: getSearchPageNumber(page),
        })),
      ) ??
      [];
    // Offset paging can return the same person on two pages (the ranked pool
    // and exclusion set shift between fetches), which would double them in the
    // list and the review queue — keep the first occurrence only.
    const seenTalentIds = new Set<number>();
    return corpus.filter((candidate) => {
      if (hiddenTalentIds.has(candidate.talentId)) return false;
      if (seenTalentIds.has(candidate.talentId)) return false;
      seenTalentIds.add(candidate.talentId);
      return true;
    });
  }, [candidatesOverride, searchApi.data, hiddenTalentIds]);
  // What actually renders: the corpus minus candidates shortlisted this
  // session (they're in the pipeline now) and minus interests queued before
  // the last filter/tune change. `candidates` stays complete so the review
  // list and analytics ranks keep seeing them.
  const visibleCandidates = useMemo(
    () =>
      candidates.filter(
        (candidate) =>
          !shortlistDecisions.has(candidate.userId) &&
          !staleInterestedTalentIds.has(candidate.talentId),
      ),
    [candidates, shortlistDecisions, staleInterestedTalentIds],
  );
  // First result of a fresh search becomes the keyboard target. Waits for the
  // new page so it doesn't grab the outgoing list's first card.
  const isSearchFetching = !candidatesOverride && searchApi.isFetching;
  useEffect(() => {
    if (!shouldFocusFirstCardRef.current || isSearchFetching) return;
    const first = visibleCandidates.at(0);
    if (!first) return;
    shouldFocusFirstCardRef.current = false;
    setActiveTalentId(first.talentId);
  }, [isSearchFetching, visibleCandidates]);

  // Undo scroll: an un-hidden card re-mounts only after the interest cache
  // updates, so wait until it's in the list; a card that's already there
  // (interest undone) scrolls on the next render.
  useEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    const isRendered = (talentId: number) =>
      visibleCandidates.some((candidate) => candidate.talentId === talentId);
    if (!isRendered(pending.talentId)) return;
    if (
      pending.afterRemovalOf !== undefined &&
      isRendered(pending.afterRemovalOf)
    ) {
      return;
    }
    pendingScrollRef.current = null;
    scrollCardIntoView(pending.talentId);
  });

  const candidateUserIds = useMemo(
    () => candidates.map((candidate) => candidate.userId),
    [candidates],
  );

  // Detect a filter/tune change and fold the currently-interested people into
  // the stale set. Ref keeps the effect off interestedTalentIds so a new
  // interest alone never triggers it.
  const interestedTalentIdsRef = useRef(interestedTalentIds);
  interestedTalentIdsRef.current = interestedTalentIds;
  const searchParamsKey = JSON.stringify(searchParams);
  const previousSearchParamsKeyRef = useRef(searchParamsKey);
  // Iterations track the real search only; Sally mode leaves the current one
  // untouched and it picks back up on return.
  const isIterationActive = !candidatesOverride;
  const {
    beginIteration,
    recordIterationDecision,
    recordIterationResults,
    recordIterationUndo,
    rememberReviewResults,
    isReviewOpen,
    session,
    status: sessionStatus,
  } = searchStore();
  // A new result set under a live session begins the next iteration; `begin`
  // no-ops on the same key, so a remount continues the current one.
  useEffect(() => {
    if (!isIterationActive || sessionStatus !== "live" || !session) return;
    beginIteration(session.id, session.jobId, searchParamsKey);
  }, [
    beginIteration,
    isIterationActive,
    searchParamsKey,
    session,
    sessionStatus,
  ]);
  const pagesLoaded = searchApi.data?.pages.length ?? 0;
  const resultsLoaded =
    searchApi.data?.pages.reduce(
      (total, page) => total + getSearchPageResults(page).length,
      0,
    ) ?? 0;
  const resultsShown = candidates.length;
  useEffect(() => {
    if (!isIterationActive) return;
    recordIterationResults({ pagesLoaded, resultsLoaded, resultsShown });
  }, [
    isIterationActive,
    pagesLoaded,
    recordIterationResults,
    resultsLoaded,
    resultsShown,
  ]);
  // Also arms auto-focus so the hotkeys can start on the first fresh result
  // without a hover.
  // Entering an override view (Sally mode) is itself a fresh result set.
  const shouldFocusFirstCardRef = useRef(candidatesOverride !== undefined);
  const previousFocusResetKeyRef = useRef(focusResetKey);
  useEffect(() => {
    if (previousFocusResetKeyRef.current === focusResetKey) return;
    previousFocusResetKeyRef.current = focusResetKey;
    shouldFocusFirstCardRef.current = true;
  }, [focusResetKey]);
  useEffect(() => {
    if (previousSearchParamsKeyRef.current === searchParamsKey) return;
    previousSearchParamsKeyRef.current = searchParamsKey;
    shouldFocusFirstCardRef.current = true;
    setStaleInterestedTalentIds((current) => {
      const next = new Set(current);
      interestedTalentIdsRef.current.forEach((talentId) => next.add(talentId));
      return next;
    });
  }, [searchParamsKey]);
  const { isPending: isPrioPending, userIdToSummary } =
    useCustomerTopPrioritySummaries(
      jobId,
      candidateUserIds,
      hasCustomerTopPriority,
    );

  // Keep the search result for every interested talent in view so the review
  // sheet has their keyword highlights. Keyed by talentId in the store, so a
  // talent can't be held twice.
  useEffect(() => {
    rememberReviewResults(
      jobId,
      candidates.filter((candidate) =>
        interestedTalentIds.has(candidate.talentId),
      ),
    );
  }, [candidates, interestedTalentIds, jobId, rememberReviewResults]);

  // Whichever corpus drives the list owns its paging.
  const paging = overridePaging ?? {
    fetchNextPage: () => {
      void searchApi.fetchNextPage();
    },
    hasNextPage: Boolean(searchApi.hasNextPage),
    isFetchingNextPage: searchApi.isFetchingNextPage,
  };

  useEffect(() => {
    if (
      !loadMoreRef.current ||
      !paging.hasNextPage ||
      paging.isFetchingNextPage
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          paging.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadMoreRef.current);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paging.fetchNextPage,
    paging.hasNextPage,
    paging.isFetchingNextPage,
  ]);

  /* Helpers */
  // Rank of the candidate in the rendered result list — the input to
  // precision@k / MRR style ranking-quality metrics.
  const getCandidateAnalyticsMetadata = (candidateId: number) => {
    const rank = candidates.findIndex(
      (candidate) => candidate.userId === candidateId,
    );
    return {
      ...SEARCH_V2_ANALYTICS_METADATA,
      is_top_candidate: topCandidateTalentIds.has(candidateId),
      job_id: jobId,
      rank: rank === -1 ? undefined : rank + 1,
      result_count: candidates.length,
      talent_id: candidateId,
    };
  };

  const getCandidateSearchEventContext = (
    candidateId: number,
  ): SearchEventContext => {
    const metadata = getCandidateAnalyticsMetadata(candidateId);
    return {
      isTopCandidate: metadata.is_top_candidate,
      rank: metadata.rank,
      resultCount: metadata.result_count,
      searchMode: candidatesOverride ? "sally" : "standard",
      source: "search_results",
    };
  };

  const getGuardrailReasons = (candidate: SearchCandidateResult) =>
    agentGuardrails.getBlockReasons({
      candidacyStatus: candidate.candidacyStatus,
      activePipelines: candidate.activePipelines,
      lastHumanCommunication: candidate.lastHumanCommunication,
      lastCommunication: candidate.lastCommunication,
    });

  /* Handlers */
  const handleShortlist = (candidateId: number, decision: ReviewDecision) => {
    // Agentic shortlists get a confirmation when a guardrail applies (past
    // submit stage, human outreach in the last 24h). Manual mode skips it.
    if (decision === ReviewDecision.SHORTLIST) {
      const candidate = candidates.find(
        (searchCandidate) => searchCandidate.userId === candidateId,
      );
      const reasons = candidate ? getGuardrailReasons(candidate) : [];
      if (reasons.length > 0) {
        setAgentGuardrail({ candidateId, reasons });
        return;
      }
    }
    performShortlist(candidateId, decision);
  };

  const performShortlist = (candidateId: number, decision: ReviewDecision) => {
    // Sally mode deliberately surfaces candidates who asked us to stop, so they
    // can be seen and worked around — never texted. Shortlisting one is fine;
    // handing it to the agent is not. guac enforces this too (it can't trust a
    // client flag), but asking for outreach we know is wrong would leave the
    // staffer with a shortlist whose behaviour silently disagrees with the UI.
    // Matched on userId because that's what the caller passes (the sheet hands
    // over `selectedCandidate.userId`). Matching talentId here would rely on
    // the two ids being interchangeable, and a lookup miss fails OPEN — it
    // would read as "not opted out" and hand the candidate to the agent.
    const isOptedOut =
      candidates.find((candidate) => candidate.userId === candidateId)
        ?.sallyState === SallyState.OPTED_OUT;
    const agentShouldReachOutIfEnabled =
      decision === ReviewDecision.SHORTLIST && !isOptedOut;
    createCandidacy(
      {
        createCandidacyRequest: {
          searchAnalytics: getCandidateSearchEventContext(candidateId),
          source: CandidacySource.LABORUPSEARCH,
          status: CandidacyStatus.SHORTLIST,
          talentId: candidateId,
          agentShouldReachOutIfEnabled,
        },
        currentJobId: jobId,
      },
      {
        onSuccess: (createdCandidacy) => {
          const candidate = candidates.find(
            (searchCandidate) => searchCandidate.userId === candidateId,
          );
          onShortlisted({
            candidateName: [candidate?.firstName, candidate?.lastName]
              .filter(Boolean)
              .join(" "),
            coinsAwarded: createdCandidacy.stafferPetAward?.coinsAwarded ?? 0,
            goalBonusCoins:
              createdCandidacy.stafferPetGoalAward?.coinsAwarded ?? 0,
          });
          setShortlistDecisions((currentDecisions) =>
            new Map(currentDecisions).set(candidateId, decision),
          );
          setCandidacyIds((currentIds) =>
            new Map(currentIds).set(candidateId, createdCandidacy.candidacy.id),
          );
          // Shortlisted candidates are in the pipeline now — clear their
          // search interest so the row doesn't linger in the review flow.
          if (interestedTalentIds.has(candidateId)) {
            onSearchInterestChange(candidateId, null, {
              reason: "shortlist_cleanup",
              searchAnalytics: getCandidateSearchEventContext(candidateId),
            });
          }
          // Shortlisting from the sheet is a terminal action — close it.
          setSelectedCandidate((current) =>
            current?.userId === candidateId ? null : current,
          );
        },
      },
    );
  };

  const handleHide = (talentId: number) => {
    const candidate = candidates.find(
      (searchCandidate) => searchCandidate.talentId === talentId,
    );
    recordDecision(talentId, SearchDecision.HIDDEN);
    onSearchInterestChange(talentId, false, {
      searchAnalytics: candidate
        ? getCandidateSearchEventContext(candidate.userId)
        : undefined,
    });
  };

  const handleInterestedToggle = (
    talentId: number,
    { isToggle = true }: { isToggle?: boolean } = {},
  ) => {
    const isInterested = !interestedTalentIds.has(talentId);
    // Hotkeys only mark interest; pressing 1 on an interested card is a no-op.
    if (!isToggle && !isInterested) return;
    const candidate = candidates.find(
      (searchCandidate) => searchCandidate.talentId === talentId,
    );
    recordDecision(
      talentId,
      isInterested ? SearchDecision.INTERESTED : SearchDecision.UNINTERESTED,
    );
    onSearchInterestChange(talentId, isInterested ? true : null, {
      reason: isInterested ? undefined : "uninterested",
      searchAnalytics: candidate
        ? getCandidateSearchEventContext(candidate.userId)
        : undefined,
    });
  };

  const getInterestState = (talentId: number): boolean | null => {
    const interest = searchInterests.find(
      (searchInterest) => searchInterest.talentId === talentId,
    );
    return interest ? interest.isInterested : null;
  };

  const publishLastDecision = () => {
    const last = undoStackRef.current.at(-1);
    onLastDecisionChange?.(
      last
        ? {
            candidateName: last.candidateName,
            decision: last.decision,
            talentId: last.talentId,
            undo: () => {
              handleUndoLastDecisionRef.current();
            },
          }
        : null,
    );
  };

  const recordDecision = (talentId: number, decision: SearchDecision) => {
    const candidate = candidates.find(
      (searchCandidate) => searchCandidate.talentId === talentId,
    );
    undoStackRef.current.push({
      candidateName: candidate
        ? `${candidate.firstName} ${candidate.lastName}`.trim()
        : "Candidate",
      decision,
      previous: getInterestState(talentId),
      talentId,
    });
    if (isIterationActive) recordIterationDecision(decision);
    publishLastDecision();
  };

  const handleUndoLastDecision = () => {
    const last = undoStackRef.current.pop();
    if (!last) return;
    mixpanelUtils.trackKeyboardShortcutUsed("U (Undo)", MIXPANEL_SCREENS.JOB);
    if (isIterationActive) recordIterationUndo();
    onSearchInterestChange(last.talentId, last.previous, { reason: "undo" });
    setActiveTalentId(last.talentId);
    // Un-hiding re-mounts the card only after the interest cache updates, so
    // the scroll waits for it to appear in the list.
    pendingScrollRef.current = { talentId: last.talentId };
    publishLastDecision();
  };

  // Stable refs so the hotkey listener attaches once.
  const handleHideRef = useRef(handleHide);
  handleHideRef.current = handleHide;
  const handleInterestedToggleRef = useRef(handleInterestedToggle);
  handleInterestedToggleRef.current = handleInterestedToggle;
  // Any overlay — search sheet, review modal, agent guardrail — owns the
  // keyboard; the list's hotkeys must not act on cards behind it.
  const isOverlayOpen =
    selectedCandidate !== null || isReviewOpen || agentGuardrail !== null;
  const isOverlayOpenRef = useRef(isOverlayOpen);
  isOverlayOpenRef.current = isOverlayOpen;
  const handleUndoLastDecisionRef = useRef(handleUndoLastDecision);
  handleUndoLastDecisionRef.current = handleUndoLastDecision;
  const visibleCandidatesRef = useRef(visibleCandidates);
  visibleCandidatesRef.current = visibleCandidates;

  // Move the keyboard target to the card after `talentId` and glide it into
  // view so 1 / 2 can be pressed straight through the list.
  // Move the keyboard target to the card after `talentId` and glide it into
  // view. When the card is being hidden, wait for it to leave first so the
  // scroll is measured against the settled layout.
  const advanceActiveCard = (
    talentId: number,
    { isLeaving = false }: { isLeaving?: boolean } = {},
  ) => {
    const list = visibleCandidatesRef.current;
    const index = list.findIndex(
      (candidate) => candidate.talentId === talentId,
    );
    const next = list.at(index + 1);
    setActiveTalentId(next?.talentId ?? null);
    if (!next) return;
    pendingScrollRef.current = {
      afterRemovalOf: isLeaving ? talentId : undefined,
      talentId: next.talentId,
    };
  };
  const advanceActiveCardRef = useRef(advanceActiveCard);
  advanceActiveCardRef.current = advanceActiveCard;

  // 1 = interest, 2 = hide, on the active card; both then advance to the next.
  // U reverts the most recent of those. The sheet owns its own keys.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isUserTyping() || isOverlayOpenRef.current) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() === "u") {
        event.preventDefault();
        handleUndoLastDecisionRef.current();
        return;
      }
      const talentId = activeTalentIdRef.current;
      if (talentId === null) return;
      if (event.key === "1") {
        event.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "1 (Interested)",
          MIXPANEL_SCREENS.JOB,
        );
        handleInterestedToggleRef.current(talentId, { isToggle: false });
        advanceActiveCardRef.current(talentId);
      } else if (event.key === "2") {
        event.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "2 (Hide)",
          MIXPANEL_SCREENS.JOB,
        );
        advanceActiveCardRef.current(talentId, { isLeaving: true });
        handleHideRef.current(talentId);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleCandidateSelect = (candidate: SearchCandidateResult) => {
    mixpanelUtils.trackRowClick(
      "Candidate",
      MIXPANEL_SCREENS.JOB,
      getCandidateAnalyticsMetadata(candidate.userId),
    );
    setSelectedCandidate(candidate);
  };

  /* Render */
  const renderCandidateContent = (loadMoreTriggerIndex: number) => {
    return (
      <Column className="w-full">
        {visibleCandidates.map((candidate, index) => (
          <Fragment key={`${String(candidate.userId)}-${String(index)}`}>
            <SearchV2CandidateCard
              candidate={candidate}
              isInterestedSelected={interestedTalentIds.has(candidate.talentId)}
              isPrioPending={isPrioPending}
              isTopCandidate={topCandidateTalentIds.has(candidate.talentId)}
              isActive={activeTalentId === candidate.talentId}
              onHide={handleHide}
              onHoverChange={(talentId) => {
                if (Date.now() < suppressHoverUntilRef.current) return;
                if (talentId !== activeTalentIdRef.current) {
                  setActiveTalentId(talentId);
                }
              }}
              onHoverLeave={(talentId) => {
                if (Date.now() < suppressHoverUntilRef.current) return;
                // Leaving a card the keys already moved on from must not
                // clear the card they moved to.
                if (talentId === activeTalentIdRef.current) {
                  setActiveTalentId(null);
                }
              }}
              onInterestedToggle={handleInterestedToggle}
              onSelect={handleCandidateSelect}
              prioSummary={userIdToSummary[candidate.userId]}
            />
            {isPageBoundary(visibleCandidates, index) && (
              <SearchPageBreak reviewQueueCount={interestedTalentIds.size} />
            )}
            {paging.hasNextPage && index === loadMoreTriggerIndex && (
              <Row className="h-px" ref={loadMoreRef} />
            )}
          </Fragment>
        ))}
      </Column>
    );
  };

  const render = () => {
    // A disabled query stays `pending` forever, so the override path must skip
    // both the loading and error branches — its data never arrives through
    // searchApi at all.
    if (!candidatesOverride && searchApi.isPending) {
      return (
        <Column className="w-full">
          {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
            <SearchV2CandidateSkeleton key={index} />
          ))}
        </Column>
      );
    }
    if (!candidatesOverride && searchApi.isError) {
      return (
        <Column className="items-center gap-3 py-16">
          <TriangleAlertIcon className="h-6 w-6 text-red-500" />
          <Column className="items-center gap-1">
            <Text className="text-sm font-semibold text-slate-800">
              Something went wrong searching for candidates
            </Text>
            <Text className="text-sm font-normal text-slate-500">
              Please try refreshing, if this continues let the engineers know!
            </Text>
          </Column>
        </Column>
      );
    }
    // A page can come back empty because every row on it was hidden, not because
    // the corpus is exhausted. Keep the trigger mounted so the next page still
    // loads — otherwise the observer has nothing to watch and the list dead-ends
    // with pages left unread.
    if (!visibleCandidates.length && paging.hasNextPage) {
      return (
        <Row className="items-center justify-center py-8" ref={loadMoreRef}>
          {paging.isFetchingNextPage && (
            <Row className="items-center gap-2">
              <Loader2Icon className="h-5 w-5 animate-spin text-primary-900" />
              <Text className="text-sm font-normal text-slate-500">
                Loading more candidates...
              </Text>
            </Row>
          )}
        </Row>
      );
    }
    if (!visibleCandidates.length) {
      return (
        <Column className="items-center gap-3 py-16">
          <SearchXIcon className="h-6 w-6 text-slate-400" />
          <Column className="items-center gap-1">
            <Text className="text-sm font-semibold text-slate-800">
              No candidates match this search
            </Text>
            <Text className="text-sm font-normal text-slate-500">
              Try broadening the match style, widening the radius, or removing a
              filter.
            </Text>
          </Column>
        </Column>
      );
    }
    const loadMoreTriggerIndex =
      Math.ceil(visibleCandidates.length * LOAD_MORE_TRIGGER_RATIO) - 1;

    return (
      <>
        {renderCandidateContent(loadMoreTriggerIndex)}
        {paging.hasNextPage && (
          <Row className="items-center justify-center py-8">
            {paging.isFetchingNextPage && (
              <Row className="items-center gap-2">
                <Loader2Icon className="h-5 w-5 animate-spin text-primary-900" />
                <Text className="text-sm font-normal text-slate-500">
                  Loading more candidates...
                </Text>
              </Row>
            )}
          </Row>
        )}
        {!paging.hasNextPage && (
          <Row className="items-center justify-center py-8">
            <Text className="text-sm font-normal text-slate-500">
              No more results
            </Text>
          </Row>
        )}
        {agentGuardrail && (
          <AgentSchedulingGuardrailDialog
            onClose={() => {
              setAgentGuardrail(null);
            }}
            onShortlistManually={() => {
              performShortlist(
                agentGuardrail.candidateId,
                ReviewDecision.SHORTLIST_MANUAL,
              );
            }}
            onUseAgent={() => {
              performShortlist(
                agentGuardrail.candidateId,
                ReviewDecision.SHORTLIST,
              );
            }}
            reasons={agentGuardrail.reasons}
            screenName={MIXPANEL_SCREENS.JOB}
          />
        )}
        {selectedCandidate && (
          <SearchSheet
            candidate={selectedCandidate}
            candidacyId={candidacyIds.get(selectedCandidate.userId)}
            isAgentActive={isAgentActive}
            isInterestedSelected={interestedTalentIds.has(
              selectedCandidate.talentId,
            )}
            onInterestedToggle={(talentId) => {
              const isInteresting = !interestedTalentIds.has(talentId);
              handleInterestedToggle(talentId);
              // Marking interested is a terminal action from the sheet — close
              // it. Un-interesting keeps it open for further review.
              if (isInteresting)
                setTimeout(() => {
                  setSelectedCandidate(null);
                }, 300);
            }}
            isShortlisted={
              selectedCandidate.candidacyStatus === CandidacyStatus.SHORTLIST ||
              shortlistDecisions.has(selectedCandidate.userId)
            }
            isShortlisting={isCreatingCandidacy}
            onClose={() => {
              setSelectedCandidate(null);
            }}
            onHide={() => {
              handleHide(selectedCandidate.talentId);
              setTimeout(() => {
                setSelectedCandidate(null);
              }, 300);
            }}
            onShortlist={(decision) => {
              handleShortlist(selectedCandidate.userId, decision);
            }}
            shortlistedDecision={shortlistDecisions.get(
              selectedCandidate.userId,
            )}
          />
        )}
      </>
    );
  };

  return render();
};
